import React, { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import './AddEventPage.css';

const INITIAL = {
  enabled:     true,
  auth_key:    '',
  template_id: '',
  sender_id:   '',
};

export default function OtpConfigPage() {
  const [form,       setForm]       = useState(INITIAL);
  const [loaded,     setLoaded]     = useState(false);
  const [errors,     setErrors]     = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [toast,      setToast]      = useState(null);
  const [showKey,    setShowKey]    = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'admin_config', 'otp'));
        if (snap.exists()) setForm({ ...INITIAL, ...snap.data() });
      } catch { /* defaults */ }
      setLoaded(true);
    })();
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setForm(p => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
    setErrors(p => ({ ...p, [name]: undefined }));
  }, []);

  const validate = useCallback(() => {
    const e = {};
    if (!form.auth_key.trim()) e.auth_key = 'Auth Key is required.';
    if (!form.template_id.trim()) e.template_id = 'Template ID is required.';
    if (form.sender_id.trim() && !/^[A-Za-z0-9]{3,11}$/.test(form.sender_id.trim()))
      e.sender_id = 'Sender ID must be 3–11 letters/digits (e.g. VORTIX).';
    return e;
  }, [form]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSubmitting(true);
    try {
      await setDoc(doc(db, 'admin_config', 'otp'), {
        enabled:     form.enabled,
        auth_key:    form.auth_key.trim(),
        template_id: form.template_id.trim(),
        sender_id:   form.sender_id.trim(),
        updated_at:  serverTimestamp(),
      });
      setToast({ type: 'success', message: '✅ SMS / OTP settings saved.' });
    } catch (err) {
      setToast({ type: 'error', message: `❌ Failed: ${err.message}` });
    } finally {
      setSubmitting(false);
    }
  }, [form, validate]);

  if (!loaded) {
    return <div className="mem-loading"><span className="tf-spinner tf-spinner--red" /></div>;
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">SMS / OTP Settings</h1>
          <p className="page-subtitle">
            MSG91 credentials used to send and verify the login OTP.
            Stored securely (admin-only) and read by the Cloud Functions.
          </p>
        </div>
      </div>

      {toast && (
        <div className={`amf-toast amf-toast--${toast.type}`} style={{ marginBottom: 24 }}>
          {toast.message}
          <button className="amf-toast-close" onClick={() => setToast(null)}>×</button>
        </div>
      )}

      <form className="aep-form" onSubmit={handleSubmit} noValidate>
        <div style={{ maxWidth: 620 }}>
          <SectionCard title="🔐 MSG91 Credentials">
            <Field label="MSG91 Auth Key" required error={errors.auth_key}>
              <div style={{ position: 'relative' }}>
                <input name="auth_key"
                  type={showKey ? 'text' : 'password'}
                  autoComplete="off"
                  className={`amf-input ${errors.auth_key ? 'amf-input--error' : ''}`}
                  placeholder="e.g. 41xxxxxxxxxxxxxxxxxxxx"
                  value={form.auth_key} onChange={handleChange}
                  style={{ paddingRight: 64 }} />
                <button type="button"
                  onClick={() => setShowKey(s => !s)}
                  style={{
                    position: 'absolute', top: '50%', right: 8,
                    transform: 'translateY(-50%)',
                    padding: '4px 10px', fontSize: 11, fontWeight: 700,
                    background: 'transparent', cursor: 'pointer',
                    color: '#D4B574',
                    border: '1px solid rgba(212, 181, 116, 0.4)',
                    borderRadius: 6,
                  }}>
                  {showKey ? 'HIDE' : 'SHOW'}
                </button>
              </div>
              <p className="amf-hint">
                From MSG91 → Settings → API → Auth Key. Kept private; never
                exposed to the app.
              </p>
            </Field>

            <Field label="MSG91 Template ID" required error={errors.template_id}>
              <input name="template_id"
                className={`amf-input ${errors.template_id ? 'amf-input--error' : ''}`}
                placeholder="e.g. 6a3fc5c3d9cc934b2d096103"
                value={form.template_id} onChange={handleChange} />
              <p className="amf-hint">
                The OTP template's ID from MSG91 (must contain <code>##OTP##</code>),
                set up for a 6-digit code.
              </p>
            </Field>

            <Field label="Sender ID" error={errors.sender_id}>
              <input name="sender_id"
                className={`amf-input ${errors.sender_id ? 'amf-input--error' : ''}`}
                placeholder="VORTIX"
                value={form.sender_id} onChange={handleChange} />
              <p className="amf-hint">
                Optional. Your 6-character DLT-approved sender header
                (e.g. <code>TFRONX</code>). Leave blank to use the template's
                default sender.
              </p>
            </Field>
          </SectionCard>

          <SectionCard title="🟢 OTP Login Status">
            <label style={{
              display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
            }}>
              <input name="enabled" type="checkbox"
                checked={form.enabled} onChange={handleChange}
                style={{ width: 18, height: 18 }} />
              <span style={{ color: '#D8C8A4', fontSize: 14 }}>
                OTP login is <strong>{form.enabled ? 'ON' : 'OFF'}</strong>
              </span>
            </label>
            <p className="amf-hint" style={{ marginTop: 8 }}>
              Turn off to temporarily block all OTP logins (e.g. if you run out
              of SMS credits). Members already signed in stay signed in.
            </p>
          </SectionCard>
        </div>

        <div className="aep-submit-row">
          <button type="submit" className="amf-submit-btn" disabled={submitting}>
            {submitting
              ? <><span className="amf-spinner" /> Saving…</>
              : 'Save SMS / OTP Settings'}
          </button>
        </div>
      </form>

      <div style={{
        marginTop: 8, maxWidth: 620,
        padding: 16,
        background: 'rgba(212, 181, 116, 0.06)',
        border: '1px solid rgba(212, 181, 116, 0.22)',
        borderRadius: 10,
      }}>
        <div style={{
          color: '#D4B574', fontSize: 11, fontWeight: 700,
          letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 8,
        }}>
          How it works
        </div>
        <div style={{ color: '#C9B486', fontSize: 13, lineHeight: 1.6 }}>
          When a registered member taps <strong>Send OTP</strong>, MSG91 sends
          a 6-digit code via SMS. The app verifies it through a secure Cloud
          Function, which then signs the member in. Make sure the number exists
          under <strong>All Members</strong> with status <strong>active</strong>,
          and that MSG91 IP whitelisting is OFF (the functions use dynamic IPs).
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, error, children, style }) {
  return (
    <div className="amf-field" style={style}>
      <label className="amf-label">
        {label}
        {required && <span className="amf-required"> *</span>}
      </label>
      {children}
      {error && <p className="amf-error-text">{error}</p>}
    </div>
  );
}

function SectionCard({ title, children }) {
  return (
    <div style={{
      marginBottom: 20, padding: 16,
      background: 'rgba(212, 181, 116, 0.04)',
      border: '1px solid rgba(212, 181, 116, 0.18)',
      borderRadius: 10,
    }}>
      <div style={{
        color: '#D4B574', fontSize: 11, letterSpacing: 1.5,
        textTransform: 'uppercase', fontWeight: 700, marginBottom: 12,
      }}>
        {title}
      </div>
      {children}
    </div>
  );
}
